import app from 'flarum/forum/app';
import humanTime from 'flarum/common/helpers/humanTime';
import sortTags from 'ext:flarum/tags/common/utils/sortTags';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import Tooltip from 'flarum/common/components/Tooltip';
import { trans, iconColors, tagRoute, navigate, getFeaturedTagIds, resolveAssetUrl } from '../utils';
function renderTagCard(tag, featured) {
  if (featured === void 0) {
    featured = false;
  }
  const color = tag.color?.();
  const {
    bg: iconBg,
    color: iconColor
  } = iconColors(color, 0.12);
  const href = tagRoute(tag);
  const lastDisc = tag.lastPostedDiscussion?.();
  const children = sortTags((tag.children?.() || []).filter(Boolean));
  const count = tag.discussionCount?.() || 0;
  const lastDiscHref = (() => {
    if (!lastDisc) return href;
    try {
      return app.route.discussion(lastDisc, lastDisc.lastPostNumber?.());
    } catch {
      return href;
    }
  })();
  return m("li", {
    key: tag.id(),
    className: `AvocadoTagsPage-tagCard${featured ? ' AvocadoTagsPage-tagCard--featured' : ''}`
  }, featured && m(Tooltip, {
    text: trans('ramon-avocado.forum.tags.featured', 'Featured'),
    position: "top"
  }, m("span", {
    className: "AvocadoTagsPage-featuredBadge"
  }, m("img", {
    src: resolveAssetUrl('fire.webp') || '',
    alt: "",
    "aria-hidden": "true",
    width: "20",
    height: "20"
  }))), m("a", {
    className: "AvocadoTagsPage-tagCard-main",
    href: href,
    onclick: e => navigate(e, href)
  }, m("div", {
    className: "AvocadoTagsPage-tagCard-top"
  }, m("span", {
    className: "AvocadoTagsPage-tagCard-icon",
    style: {
      '--icon-bg': iconBg,
      '--icon-color': iconColor
    }
  }, m("i", {
    className: tag.icon?.() || 'fas fa-tag',
    "aria-hidden": "true"
  })), m("div", {
    className: "AvocadoTagsPage-tagCard-info"
  }, m("h2", {
    className: "AvocadoTagsPage-tagCard-name"
  }, tag.name?.()), m("span", {
    className: "AvocadoTagsPage-tagCard-count"
  }, count, ' ', count === 1 ? trans('ramon-avocado.forum.tags.discussion_singular', 'discussion') : trans('ramon-avocado.forum.tags.discussion_plural', 'discussions')))), tag.description?.() && m("p", {
    className: "AvocadoTagsPage-tagCard-desc"
  }, tag.description())), children.length > 0 && m("div", {
    className: "AvocadoTagsPage-tagCard-children"
  }, children.map(child => {
    const childColor = child.color?.() || color;
    const childHref = tagRoute(child);
    const ic = iconColors(childColor, 0.1);
    return m("a", {
      key: child.id(),
      className: "AvocadoTagsPage-childPill",
      href: childHref,
      onclick: e => {
        e.stopPropagation();
        navigate(e, childHref);
      },
      style: {
        '--cp-bg': ic.bg,
        '--cp-color': ic.color
      }
    }, child.icon?.() && m("i", {
      className: child.icon(),
      "aria-hidden": "true"
    }), child.name?.());
  })), lastDisc && m("a", {
    className: "AvocadoTagsPage-tagCard-last",
    href: lastDiscHref,
    onclick: e => {
      e.stopPropagation();
      navigate(e, lastDiscHref);
    }
  }, m("i", {
    className: "far fa-clock",
    "aria-hidden": "true"
  }), m("span", {
    className: "AvocadoTagsPage-tagCard-last-title"
  }, lastDisc.title?.()), m("span", {
    className: "AvocadoTagsPage-tagCard-last-time"
  }, humanTime(lastDisc.lastPostedAt?.()))));
}
function renderSkeleton() {
  return [0, 1, 2, 3].map(i => m("li", {
    key: String(i),
    className: "AvocadoTagsPage-tagCard AvocadoTagsPage-tagCard--skeleton"
  }, m("div", {
    className: "AvocadoTagsPage-tagCard-main AvocadoTagsPage-tagCard-main--nolink"
  }, m("div", {
    className: "AvocadoTagsPage-tagCard-top"
  }, m("span", {
    className: "AvocadoTagsPage-tagCard-icon AvocadoTagsPage-shimmer"
  }), m("div", {
    className: "AvocadoTagsPage-tagCard-info"
  }, m("div", {
    className: "AvocadoTagsPage-shimmer AvocadoTagsPage-shimmer--name"
  }), m("div", {
    className: "AvocadoTagsPage-shimmer AvocadoTagsPage-shimmer--count"
  }))), m("div", {
    className: "AvocadoTagsPage-shimmer AvocadoTagsPage-shimmer--desc"
  }))));
}

/** Override target for TagsPage.prototype.view — `this` = native Flarum TagsPage instance */
export function tagPageView(_original) {
  const tags = this.tags || [];
  const loading = !!this.loading;
  const featuredIds = getFeaturedTagIds();
  const primaryTags = tags.filter(t => t.position?.() !== null).sort((a, b) => {
    const aF = featuredIds.has(String(a.id()));
    const bF = featuredIds.has(String(b.id()));
    if (aF !== bF) return aF ? -1 : 1;
    return (a.position?.() ?? 9999) - (b.position?.() ?? 9999);
  });
  const cloudTags = tags.filter(t => t.position?.() === null);
  const homeHref = (() => {
    try {
      return app.route('index');
    } catch {
      return '/';
    }
  })();
  const discHref = (() => {
    try {
      return app.route('avocado-discussions');
    } catch {
      return '/discussions';
    }
  })();
  return m("div", {
    className: "AvocadoTagsPage"
  }, m("div", {
    className: "AvocadoNav-helper"
  }, m(IndexSidebar, null)), m("div", {
    className: "AvocadoTagsPage-header"
  }, m("h1", {
    className: "AvocadoTagsPage-title"
  }, trans('ramon-avocado.forum.tags.title', 'Categories')), m("div", {
    className: "AvocadoTagsPage-headerActions"
  }, m("a", {
    className: "AvocadoTagsPage-headerLink",
    href: discHref,
    onclick: e => navigate(e, discHref)
  }, m("i", {
    className: "fas fa-list",
    "aria-hidden": "true"
  }), trans('ramon-avocado.forum.tags.all_discussions', 'All discussions')), m("a", {
    className: "AvocadoTagsPage-headerLink",
    href: homeHref,
    onclick: e => navigate(e, homeHref)
  }, m("i", {
    className: "fas fa-house",
    "aria-hidden": "true"
  }), trans('ramon-avocado.forum.tags.home', 'Home')))), m("ul", {
    className: "AvocadoTagsPage-grid"
  }, loading ? renderSkeleton() : primaryTags.map(tag => renderTagCard(tag, featuredIds.has(String(tag.id()))))), !loading && cloudTags.length > 0 && m("div", {
    className: "AvocadoTagsPage-cloud"
  }, m("p", {
    className: "AvocadoTagsPage-cloud-label"
  }, trans('ramon-avocado.forum.tags.other_tags_label', 'Other Tags')), m("div", {
    className: "AvocadoTagsPage-cloud-pills"
  }, cloudTags.map(tag => {
    const color = tag.color?.();
    const href = tagRoute(tag);
    const count = tag.discussionCount?.() || 0;
    const ic = iconColors(color, 0.1);
    return m("a", {
      key: tag.id(),
      className: "AvocadoTagsPage-cloudPill",
      href: href,
      onclick: e => navigate(e, href),
      style: {
        '--cp-bg': ic.bg,
        '--cp-color': ic.color
      }
    }, tag.icon?.() && m("i", {
      className: tag.icon(),
      "aria-hidden": "true"
    }), tag.name?.(), count > 0 && m("span", {
      className: "AvocadoTagsPage-cloudPill-count"
    }, count));
  }))));
}
flarum.reg.add('ramon-avocado', 'forum/components/TagsPage', { tagPageView: tagPageView, });